import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Loader2, Film, FileText, LayoutGrid, Save, Volume2, Captions, Image as ImageIcon, Zap, Music, Play, Pause, X, Disc3 } from "lucide-react";
import { toast } from "sonner";

import { visionApi, PROJECT_TYPE_META, STATUS_META } from "@/lib/visionApi";
import { api } from "@/lib/api";
import AiWriteScriptModal, { AiWriteButton } from "@/components/AiWriteScriptModal";
import ContentLanguageSelect from "@/components/ContentLanguageSelect";
import { appConfirm } from "@/components/AppDialogHost";

const fmtTs = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const Section = ({ title, icon: Icon, action, children, testid }) => (
  <div className="rounded-xl border border-line bg-bg-surface p-5 mb-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <div className="font-display text-lg font-bold flex items-center gap-2">
        {Icon ? <Icon className="h-5 w-5 text-brand-blue" /> : null} {title}
      </div>
      {action}
    </div>
    {children}
  </div>
);

export default function VisionProject() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [storyboardLoading, setStoryboardLoading] = useState(false);

  const load = async () => {
    try { setP(await visionApi.getProject(id)); }
    catch { toast.error("Project not found"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const runScript = async () => {
    if (scriptLoading) return;
    setScriptLoading(true);
    toast.message("Generating script — Claude is writing, ~15s");
    try {
      const r = await visionApi.genScript(id);
      toast.success("Script generated");
      setP((prev) => ({ ...prev, script: r.script, status: "scripted" }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Script generation failed"); }
    finally { setScriptLoading(false); }
  };

  const runStoryboard = async () => {
    if (storyboardLoading) return;
    if (!p?.script) { toast.error("Generate a script first"); return; }
    setStoryboardLoading(true);
    toast.message("Generating storyboard — ~15s");
    try {
      const r = await visionApi.genStoryboard(id);
      toast.success("Storyboard generated");
      setP((prev) => ({ ...prev, storyboard: r.storyboard, status: "storyboarded" }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Storyboard failed"); }
    finally { setStoryboardLoading(false); }
  };

  const [voiceLoading, setVoiceLoading] = useState(false);
  const [captionsLoading, setCaptionsLoading] = useState(false);
  const [thumbLoading, setThumbLoading] = useState(false);
  const [runAllLoading, setRunAllLoading] = useState(false);

  const runVoice = async () => {
    if (!p?.script) { toast.error("Generate a script first"); return; }
    setVoiceLoading(true);
    toast.message("Generating voiceover — ~10s");
    try {
      const r = await api.post(`/vision/projects/${id}/voice`, { voice: "nova", model: "tts-1", speed: 1.0 });
      toast.success(`Voice generated · ~${r.data.voice.estimated_duration_seconds}s`);
      setP((prev) => ({ ...prev, voice: r.data.voice, status: "voiced" }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Voice failed"); }
    finally { setVoiceLoading(false); }
  };

  const runCaptions = async () => {
    if (!p?.voice) { toast.error("Generate voice first"); return; }
    setCaptionsLoading(true);
    toast.message("Transcribing with Whisper — ~10s");
    try {
      const r = await api.post(`/vision/projects/${id}/captions`);
      toast.success("Captions generated (SRT, VTT, TXT)");
      setP((prev) => ({ ...prev, captions: r.data.captions }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Captions failed"); }
    finally { setCaptionsLoading(false); }
  };

  // Language Center · Update #133 — translate an existing captions.srt into
  // another language (Whisper transcribes in-language, the LLM then localises
  // the text while ffmpeg/burn-in reads back the ISO code we hand it).
  const [translateLang, setTranslateLang] = useState("es");
  const [translateBusy, setTranslateBusy] = useState(false);
  const runTranslateCaptions = async () => {
    if (!p?.captions) { toast.error("Generate captions first"); return; }
    if (translateLang === (p?.captions?.language || "en")) {
      toast.error("Captions are already in that language."); return;
    }
    setTranslateBusy(true);
    toast.message(`Translating captions to ${translateLang.toUpperCase()} — ~15s`);
    try {
      const r = await api.post(`/vision/projects/${id}/captions/translate`, {
        target_lang: translateLang,
      });
      toast.success(`Captions translated · ${r.data.cue_count} cues`);
      // Merge translation into the local project doc so UI updates immediately
      setP((prev) => ({
        ...prev,
        captions: {
          ...(prev.captions || {}),
          translations: [
            ...((prev.captions?.translations || []).filter((t) => t.language !== r.data.language)),
            {
              language: r.data.language,
              language_name: r.data.language_name,
              files: r.data.files,
            },
          ],
        },
      }));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) toast.error("AI budget exhausted — top up Universal Key.", { duration: 8000 });
      else toast.error(e?.response?.data?.detail || "Translation failed");
    } finally { setTranslateBusy(false); }
  };

  const runThumb = async () => {
    setThumbLoading(true);
    toast.message("Generating thumbnails (Nano Banana) — ~25s");
    try {
      const r = await api.post(`/vision/projects/${id}/thumbnail`, { platforms: ["youtube", "instagram"] });
      const okCount = (r.data.thumbnails.items || []).filter((x) => x.ok).length;
      toast.success(`${okCount} thumbnail(s) generated`);
      setP((prev) => ({ ...prev, thumbnails: r.data.thumbnails }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Thumbnail failed"); }
    finally { setThumbLoading(false); }
  };

  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const runAll = async () => {
    if (!await appConfirm("Run the full pipeline (script → storyboard → voice → captions → thumbnails)? Takes ~90 seconds.")) return;
    // User gesture — good moment to ask for browser notification permission
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch { /* */ }
    }
    setRunAllLoading(true);
    toast.message("AI Project Manager: pipeline started — progress updates live below");
    try {
      await api.post(`/vision/projects/${id}/run-all`);
      // The pipeline runs server-side in the background (a single HTTP
      // request would hit the 60s proxy timeout) — poll for progress.
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/vision/projects/${id}`);
          setP(r.data);
          const pl = r.data.pipeline || {};
          if (!pl.running) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setRunAllLoading(false);
            const okSteps = (pl.steps || []).filter((s) => s.ok).length;
            const total = (pl.steps || []).length;
            if (pl.ok) toast.success(`Full pipeline complete: all ${okSteps} steps OK`);
            else {
              const firstErr = (pl.steps || []).find((s) => !s.ok);
              toast.warning(`Pipeline finished with errors — ${okSteps}/${total} steps OK${firstErr ? ` · ${firstErr.step}: ${firstErr.error}` : ""}`, { duration: 10000 });
            }
          }
        } catch { /* transient poll failure — keep polling */ }
      }, 3000);
    } catch (e) {
      setRunAllLoading(false);
      toast.error(e?.response?.data?.detail || "Could not start the pipeline");
    }
  };

  const [audiogramLoading, setAudiogramLoading] = useState(false);

  // Music bed (Audio Studio attachment)
  const bedAudioRef = useRef(null);
  const [bedPlaying, setBedPlaying] = useState(false);
  const [bedBusy, setBedBusy] = useState(false);

  const playBed = () => {
    const url = p?.music_bed?.preview_url;
    if (!url) { toast.error("No preview URL on this track"); return; }
    if (bedPlaying) {
      bedAudioRef.current?.pause();
      setBedPlaying(false);
      return;
    }
    if (bedAudioRef.current) bedAudioRef.current.pause();
    const a = new Audio(url);
    a.onended = () => setBedPlaying(false);
    a.onerror = () => { toast.error("Couldn't play preview"); setBedPlaying(false); };
    a.play().catch(() => { toast.error("Couldn't play preview"); setBedPlaying(false); });
    bedAudioRef.current = a;
    setBedPlaying(true);
  };

  const detachBed = async () => {
    if (!await appConfirm("Remove the music bed from this project?")) return;
    setBedBusy(true);
    try {
      await api.delete(`/audio-studio/attach/${id}`);
      setP((prev) => { const next = { ...prev }; delete next.music_bed; return next; });
      bedAudioRef.current?.pause();
      setBedPlaying(false);
      toast.success("Music bed removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Detach failed");
    } finally { setBedBusy(false); }
  };

  const runAudiogram = async (aspect = "9:16") => {
    if (!p?.voice) { toast.error("Generate voice first"); return; }
    setAudiogramLoading(true);
    toast.message(`Rendering ${aspect} audiogram (FFmpeg) — ~30s`);
    try {
      const r = await api.post(`/vision/projects/${id}/audiogram`, {
        aspect, burn_captions: !!p.captions, thumbnail_platform: "instagram",
      });
      toast.success(`Audiogram ready · ${Math.round(r.data.audiogram.size_bytes / 1024)}KB`);
      setP((prev) => ({ ...prev, audiogram: r.data.audiogram }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Audiogram render failed"); }
    finally { setAudiogramLoading(false); }
  };

  // ── Phase 3 — full render + auto-publish ──
  const [renderLoading, setRenderLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [pubPlatforms, setPubPlatforms] = useState(["instagram", "youtube", "facebook"]);
  const [pubCaption, setPubCaption] = useState("");

  const runFullRender = async (aspect = "16:9") => {
    if (!p?.voice) { toast.error("Generate voice first"); return; }
    setRenderLoading(true);
    toast.message(`Rendering ${aspect} full video (FFmpeg) — this can take 1-3 min for long narrations`);
    try {
      const r = await api.post(`/vision/projects/${id}/render`, {
        aspect,
        burn_captions: !!p.captions,
      });
      toast.success(`Full render ready · ${Math.round(r.data.render.size_bytes / 1024)}KB`);
      setP((prev) => ({ ...prev, render: r.data.render, status: "rendered" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Full render failed", { duration: 10000 });
    } finally { setRenderLoading(false); }
  };

  const togglePubPlatform = (plat) => {
    setPubPlatforms((prev) => prev.includes(plat) ? prev.filter((x) => x !== plat) : [...prev, plat]);
  };

  const publishRender = async () => {
    if (!p?.render?.file) { toast.error("Render first"); return; }
    if (!pubPlatforms.length) { toast.error("Pick at least one platform"); return; }
    setPublishLoading(true);
    try {
      const r = await api.post(`/vision/projects/${id}/publish`, {
        platforms: pubPlatforms,
        caption: pubCaption,
      });
      const { total, skipped } = r.data;
      if (total > 0) toast.success(`Queued ${total} post${total === 1 ? "" : "s"}${skipped?.length ? ` · ${skipped.length} skipped (dup within 10min)` : ""}`);
      else if (skipped?.length) toast.warning(`All ${skipped.length} platform(s) skipped — already queued in the last 10 min`);
      setP((prev) => ({ ...prev, status: "published", last_published_at: new Date().toISOString() }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed", { duration: 10000 });
    } finally { setPublishLoading(false); }
  };


  const saveBrief = async (brief) => {
    try {
      const updated = await visionApi.updateProject(id, { brief });
      setP(updated);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  if (loading) return <div className="p-10 text-center font-mono text-xs text-ink-muted">Loading…</div>;
  if (!p) return <div className="p-10 text-center text-ink-muted">Project not found.</div>;

  const meta = PROJECT_TYPE_META[p.project_type] || { label: p.project_type, emoji: "🎬" };
  const status = STATUS_META[p.status] || STATUS_META.draft;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto animate-fade-up" data-testid="vision-project-page">
      <Link to="/dashboard/vision-studio" data-testid="back-to-vision" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand-amber transition mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Vision Studio
      </Link>

      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <span className="text-3xl">{meta.emoji}</span>
          <h1 className="font-display text-3xl md:text-4xl font-bold">{p.title}</h1>
          <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded ${status.bg} ${status.color}`}>{status.label}</span>
        </div>
        <div className="text-sm text-ink-muted">{meta.label} · {p.duration_seconds}s · {p.aspect} · {p.provider} · updated {fmtTs(p.updated_at)}</div>
      </div>

      <div className="mb-6 rounded-xl border border-brand-amber/40 bg-gradient-to-r from-brand-amber/10 via-bg-surface to-brand-blue/10 p-4" data-testid="ai-project-manager-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display font-bold flex items-center gap-2"><Zap className="h-5 w-5 text-brand-amber" /> AI Project Manager</div>
            <div className="text-xs text-ink-muted">One click runs the full pipeline: script → storyboard → voice → captions → thumbnails.</div>
          </div>
          <button onClick={runAll} disabled={runAllLoading || p.pipeline?.running} data-testid="run-all-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand-amber text-bg font-bold disabled:opacity-50 amber-glow">
            {(runAllLoading || p.pipeline?.running) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            {(runAllLoading || p.pipeline?.running) ? "Running…" : "Run Full Pipeline"}
          </button>
        </div>
        {(p.pipeline?.running || runAllLoading || (p.pipeline?.steps || []).length > 0) && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="pipeline-steps">
            {["script", "storyboard", "voice", "captions", "thumbnail"].map((name) => {
              const done = (p.pipeline?.steps || []).find((s) => s.step === name);
              const running = (p.pipeline?.running || runAllLoading) && !done;
              return (
                <span key={name} title={done?.error || ""}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider ${
                    done ? (done.ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-rose-500/40 bg-rose-500/10 text-rose-300")
                         : running ? "border-brand-amber/40 bg-brand-amber/10 text-brand-amber" : "border-line bg-bg-elevated text-ink-muted"
                  }`}
                  data-testid={`pipeline-step-${name}`}>
                  {done ? (done.ok ? "✓" : "✗") : running ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : "·"} {name}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <Section title="Creative Brief" icon={FileText} testid="section-brief">
        <BriefEditor brief={p.brief || ""} onSave={saveBrief} />
      </Section>

      <Section
        title="AI Script"
        icon={Sparkles}
        testid="section-script"
        action={
          <button onClick={runScript} disabled={scriptLoading} data-testid="generate-script-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-amber text-bg font-bold disabled:opacity-50">
            {scriptLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {p.script ? "Regenerate" : "Generate Script"}
          </button>
        }
      >
        {!p.script ? (
          <div className="text-sm text-ink-muted">No script yet. Click <strong>Generate Script</strong> — Claude Sonnet will write a broadcast-ready script with scenes, narration, camera directions, lower thirds and music cues. ~15 seconds.</div>
        ) : <ScriptView script={p.script} />}
      </Section>

      <Section
        title="AI Storyboard"
        icon={LayoutGrid}
        testid="section-storyboard"
        action={
          <button onClick={runStoryboard} disabled={storyboardLoading || !p.script} data-testid="generate-storyboard-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-blue text-white font-bold disabled:opacity-50">
            {storyboardLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {p.storyboard ? "Regenerate" : "Generate Storyboard"}
          </button>
        }
      >
        {!p.script ? (
          <div className="text-sm text-ink-muted">Generate the script first.</div>
        ) : !p.storyboard ? (
          <div className="text-sm text-ink-muted">No storyboard yet. Click <strong>Generate Storyboard</strong> — for each scene we&apos;ll produce a fully-detailed image-generation prompt + motion prompt, ready to send to {p.provider}.</div>
        ) : <StoryboardView storyboard={p.storyboard} />}
      </Section>

      <Section
        title="AI Voice (Narration)"
        icon={Volume2}
        testid="section-voice"
        action={
          <button onClick={runVoice} disabled={voiceLoading || !p.script} data-testid="generate-voice-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-blue text-white font-bold disabled:opacity-50">
            {voiceLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
            {p.voice ? "Regenerate Voice" : "Generate Voice"}
          </button>
        }
      >
        {!p.script ? (
          <div className="text-sm text-ink-muted">Generate the script first.</div>
        ) : !p.voice ? (
          <div className="text-sm text-ink-muted">No voiceover yet. OpenAI TTS will narrate the full script using the &ldquo;nova&rdquo; voice — ~10 seconds.</div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-ink-muted">Voice: <strong className="text-ink-primary">{p.voice.voice}</strong> · Model: {p.voice.model} · Speed: {p.voice.speed}x · ~{p.voice.estimated_duration_seconds}s · {Math.round((p.voice.size_bytes || 0) / 1024)}KB</div>
            <audio controls className="w-full" data-testid="narration-audio" src={`${process.env.REACT_APP_BACKEND_URL}/api/vision/projects/${id}/media/narration.mp3`} />
          </div>
        )}
      </Section>

      <Section
        title="Music Bed (Audio Studio)"
        icon={Music}
        testid="section-music-bed"
        action={
          <Link to="/dashboard/audio-studio" data-testid="open-audio-studio"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-brand-amber/30 bg-brand-amber/10 text-brand-amber font-bold">
            <Music className="h-3.5 w-3.5" /> Browse Audio Studio
          </Link>
        }
      >
        {!p.music_bed ? (
          <div className="text-sm text-ink-muted" data-testid="music-bed-empty">
            No music bed attached. Open <strong>Audio Studio</strong>, find a licensed track from Epidemic Sound, then tap <em>Attach</em> on the track to bind it here. The bed will be mixed under the narration in Phase 3 renders.
          </div>
        ) : (
          <div className="flex items-center gap-4 rounded-md border border-line bg-bg-elevated p-3" data-testid="music-bed-card">
            <button
              onClick={playBed}
              className="h-12 w-12 rounded-md bg-brand-amber/10 text-brand-amber grid place-items-center shrink-0 hover:bg-brand-amber/20"
              data-testid="music-bed-play"
              title={bedPlaying ? "Pause" : "Play preview"}
            >
              {bedPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </button>
            {p.music_bed.cover_url ? (
              <img src={p.music_bed.cover_url} alt="" className="h-12 w-12 rounded object-cover shrink-0" />
            ) : (
              <div className="h-12 w-12 rounded bg-bg grid place-items-center shrink-0"><Disc3 className="h-5 w-5 text-ink-muted" /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-ink-primary truncate" data-testid="music-bed-title">{p.music_bed.title || p.music_bed.track_id}</div>
              <div className="text-[11px] text-ink-muted truncate">
                {p.music_bed.artist || "—"} · {p.music_bed.provider_label || "Epidemic Sound"}
                {p.music_bed.duration_sec ? ` · ${Math.floor(p.music_bed.duration_sec / 60)}:${String(Math.floor(p.music_bed.duration_sec % 60)).padStart(2, "0")}` : ""}
              </div>
            </div>
            <button
              onClick={detachBed}
              disabled={bedBusy}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-brand-onair/30 text-brand-onair hover:bg-brand-onair/5 disabled:opacity-50"
              data-testid="music-bed-detach"
            >
              {bedBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              Detach
            </button>
          </div>
        )}
      </Section>

      <Section
        title="AI Captions"
        icon={Captions}
        testid="section-captions"
        action={
          <button onClick={runCaptions} disabled={captionsLoading || !p.voice} data-testid="generate-captions-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-blue text-white font-bold disabled:opacity-50">
            {captionsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Captions className="h-4 w-4" />}
            {p.captions ? "Regenerate" : "Generate Captions"}
          </button>
        }
      >
        {!p.voice ? (
          <div className="text-sm text-ink-muted">Generate voiceover first.</div>
        ) : !p.captions ? (
          <div className="text-sm text-ink-muted">No captions yet. Whisper will transcribe the narration to SRT, VTT and TXT — ~10 seconds.</div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-xs">
              {Object.entries(p.captions.files).map(([fmt, fn]) => (
                <a key={fmt} href={`${process.env.REACT_APP_BACKEND_URL}/api/vision/projects/${id}/media/${fn}`} target="_blank" rel="noopener noreferrer" data-testid={`caption-${fmt}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-brand-blue/40 bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/20 transition uppercase font-mono">
                  .{fmt} ↓
                </a>
              ))}
              <span className="text-ink-muted">{p.captions.char_count} chars · {p.captions.language}</span>
            </div>

            {/* Language Center · translate captions into another language */}
            <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-300">// LANGUAGE CENTER</div>
                  <div className="text-sm font-semibold text-ink-primary">Translate captions</div>
                  <div className="text-[11px] text-ink-muted">Preserves all timestamps · outputs .srt + .vtt in the target language</div>
                </div>
                <div className="flex items-end gap-2">
                  <ContentLanguageSelect
                    value={translateLang}
                    onChange={setTranslateLang}
                    testid="captions-translate-lang"
                    label={null}
                    compact
                  />
                  <button
                    onClick={runTranslateCaptions}
                    disabled={translateBusy || !p.captions}
                    data-testid="captions-translate-btn"
                    className="inline-flex items-center gap-1.5 rounded-md bg-cyan-500 text-black px-3 py-1.5 text-xs font-bold disabled:opacity-50"
                  >
                    {translateBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    Translate
                  </button>
                </div>
              </div>
              {(p.captions.translations || []).length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-cyan-500/10">
                  {(p.captions.translations || []).map((tr) => (
                    <div key={tr.language} className="flex items-center gap-1"
                      data-testid={`caption-translation-${tr.language}`}>
                      <span className="text-[10px] uppercase font-mono text-cyan-300">{tr.language}</span>
                      {Object.entries(tr.files || {}).map(([fmt, fn]) => (
                        <a
                          key={fmt}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/vision/projects/${id}/media/${fn}`}
                          target="_blank" rel="noopener noreferrer"
                          data-testid={`caption-translation-${tr.language}-${fmt}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 uppercase font-mono text-[10px]"
                        >
                          .{fmt} ↓
                        </a>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Section>

      <Section
        title="AI Thumbnails"
        icon={ImageIcon}
        testid="section-thumbnails"
        action={
          <button onClick={runThumb} disabled={thumbLoading} data-testid="generate-thumb-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-amber text-bg font-bold disabled:opacity-50">
            {thumbLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            {p.thumbnails ? "Regenerate Thumbnails" : "Generate Thumbnails"}
          </button>
        }
      >
        {!p.thumbnails ? (
          <div className="text-sm text-ink-muted">Gemini Nano Banana will generate platform-optimised thumbnails (YouTube + Instagram by default) — ~25 seconds.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(p.thumbnails.items || []).map((t, i) => (
              <div key={i} data-testid={`thumb-${t.platform}`} className="rounded-md border border-line bg-bg-elevated p-3">
                <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-2">{t.dim_hint || t.platform}</div>
                {t.ok ? (
                  <>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/vision/projects/${id}/media/${t.file}`} alt={t.platform}
                      className="w-full rounded border border-line" />
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/vision/projects/${id}/media/${t.file}`} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-brand-blue hover:underline mt-2 inline-block">Open full size ↗</a>
                  </>
                ) : (
                  <div className="text-xs text-brand-onair">Failed: {t.error}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Quick Audiogram (vertical MP4 for TikTok / Reels / Shorts)" icon={Film} testid="section-audiogram">
        <div className="text-xs text-ink-muted mb-3">
          Combines narration MP3 + thumbnail still + a brand-colored waveform animation. Captions are burned in if available. Ships before the full Phase 3 render pipeline so you can publish to vertical platforms today.
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {["9:16", "1:1", "16:9"].map((a) => (
            <button key={a} onClick={() => runAudiogram(a)} disabled={audiogramLoading || !p.voice}
              data-testid={`audiogram-btn-${a.replace(':', 'x')}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-amber text-bg font-bold disabled:opacity-50 text-sm">
              {audiogramLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />}
              Render {a}
            </button>
          ))}
        </div>
        {p.audiogram && (
          <div className="rounded-md border border-line bg-bg-elevated p-3" data-testid="audiogram-result">
            <div className="text-xs text-ink-muted mb-2">
              {p.audiogram.dimensions} · {Math.round(p.audiogram.size_bytes / 1024)}KB · {p.audiogram.captions_burned ? "captions burned" : "no captions"} · generated {fmtTs(p.audiogram.generated_at)}
            </div>
            <video controls className="w-full max-w-md rounded border border-line"
              src={`${process.env.REACT_APP_BACKEND_URL}/api/vision/projects/${id}/media/${p.audiogram.file}`} />
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/vision/projects/${id}/media/${p.audiogram.file}`} target="_blank" rel="noopener noreferrer"
              className="text-xs text-brand-blue hover:underline mt-2 inline-block">Download MP4 ↗</a>
          </div>
        )}
      </Section>

      <Section title="Render & Publish" icon={Film} testid="section-render">
        <div className="text-xs text-ink-muted mb-3">
          Phase 3: Composites narration + storyboard thumbnail + brand kit (logo + lower-third) + music bed + captions into one broadcast-ready MP4. Then queue it directly into the social auto-poster for Instagram, YouTube, Facebook, TikTok, X, and Threads.
        </div>

        {/* Render row */}
        <div className="flex flex-wrap gap-2 mb-4">
          {["16:9", "9:16", "1:1"].map((a) => (
            <button
              key={a}
              onClick={() => runFullRender(a)}
              disabled={renderLoading || !p.voice}
              data-testid={`render-btn-${a.replace(":", "x")}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-amber text-bg font-bold text-sm disabled:opacity-50 amber-glow"
            >
              {renderLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />}
              Render {a}
            </button>
          ))}
        </div>

        {/* Render result */}
        {p.render && (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 mb-4" data-testid="render-result">
            <div className="text-[11px] font-mono uppercase tracking-wider text-emerald-300 mb-2">
              Render ready · {p.render.dimensions} · {Math.round(p.render.size_bytes / 1024)}KB
              {p.render.music_bed ? " · music bed mixed" : ""}
              {p.render.captions_burned ? " · captions burned" : ""}
              {p.render.logo ? " · brand logo overlay" : ""}
            </div>
            <video controls className="w-full max-w-2xl rounded border border-line"
              src={`${process.env.REACT_APP_BACKEND_URL}/api/vision/projects/${id}/media/${p.render.file}`} />
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/vision/projects/${id}/media/${p.render.file}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="render-download"
              className="text-xs text-brand-amber hover:underline mt-2 inline-block">Download MP4 ↗</a>
          </div>
        )}

        {/* Publish row — only shows when a render exists */}
        {p.render && (
          <div className="rounded-md border border-line bg-bg-elevated p-4" data-testid="publish-block">
            <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-2">// AUTO-PUBLISH TO SOCIALS</div>
            <div className="text-xs text-ink-muted mb-3">
              Drops the rendered MP4 into the existing auto-poster queue. Existing OAuth connections under <Link to="/dashboard/social-connections" className="text-brand-amber hover:underline">Social Connections</Link> are used.
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {["instagram", "facebook", "youtube", "tiktok", "x_twitter", "threads"].map((plat) => (
                <button
                  key={plat}
                  onClick={() => togglePubPlatform(plat)}
                  data-testid={`pub-plat-${plat}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                    pubPlatforms.includes(plat)
                      ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
                      : "border-line bg-bg-surface text-ink-secondary hover:text-ink-primary"
                  }`}
                >
                  {plat.replace("_", "/")}
                </button>
              ))}
            </div>
            <textarea
              value={pubCaption}
              onChange={(e) => setPubCaption(e.target.value)}
              rows={2}
              placeholder={(p.script?.hook) || p.title || "Caption / first comment text…"}
              data-testid="pub-caption"
              className="w-full rounded-md border border-line bg-bg text-ink-primary placeholder:text-ink-muted px-3 py-2 mb-3 text-sm focus:border-brand-amber focus:outline-none"
            />
            <button
              onClick={publishRender}
              disabled={publishLoading || !pubPlatforms.length}
              data-testid="publish-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-amber text-bg font-bold text-sm disabled:opacity-50 amber-glow"
            >
              {publishLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Queue {pubPlatforms.length} post{pubPlatforms.length === 1 ? "" : "s"}
            </button>
            {p.last_published_at && (
              <div className="text-[10px] font-mono text-ink-muted mt-2">Last published: {fmtTs(p.last_published_at)}</div>
            )}
          </div>
        )}

        {!p.voice && (
          <div className="rounded-md border border-dashed border-brand-amber/40 bg-brand-amber/5 p-3 text-xs text-brand-amber">
            ⚠ Generate the voiceover above first — Phase 3 render needs a narration MP3.
          </div>
        )}
      </Section>
    </div>
  );
}

const BriefEditor = ({ brief, onSave }) => {
  const [val, setVal] = useState(brief);
  const [aiOpen, setAiOpen] = useState(false);
  const dirty = val !== brief;
  return (
    <>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase tracking-wider text-ink-muted font-mono">Creative brief</div>
        <AiWriteButton onClick={() => setAiOpen(true)} testid="vision-brief-ai-write" label="Write for me" />
      </div>
      <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={3} data-testid="brief-editor"
        placeholder="Add or refine your creative brief…"
        className="w-full rounded-md border border-line bg-bg-elevated text-ink-primary placeholder:text-ink-muted px-3 py-2 mb-2 text-sm focus:border-brand-amber focus:outline-none" />
      <button onClick={() => onSave(val)} disabled={!dirty} data-testid="save-brief"
        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-brand-blue/10 text-brand-blue border border-brand-blue/30 disabled:opacity-30">
        <Save className="h-3.5 w-3.5" /> Save brief
      </button>
      <AiWriteScriptModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        defaultScriptType="custom"
        defaultTone="conversational"
        defaultLengthSec={60}
        initialTopic={val}
        hideTitle
        onApply={({ script }) => { setVal(script); setAiOpen(false); toast.success("Brief drafted. Save it to lock it in."); }}
      />
    </>
  );
};

const ScriptView = ({ script }) => (
  <div className="space-y-3 text-sm">
    <div className="rounded-md border border-line bg-bg-elevated p-3">
      <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-1">Hook</div>
      <div className="font-display text-lg">{script.hook}</div>
    </div>
    <div className="rounded-md border border-line bg-bg-elevated p-3">
      <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-1">Voiceover (full)</div>
      <div className="whitespace-pre-line">{script.narration}</div>
    </div>
    <div className="space-y-2">
      <div className="text-[11px] uppercase tracking-wider text-ink-muted">Scenes ({script.scenes?.length || 0})</div>
      {(script.scenes || []).map((s, i) => (
        <div key={i} className="rounded-md border border-line bg-bg-elevated p-3" data-testid={`script-scene-${i}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="font-display font-bold">Scene {i + 1}: {s.title}</div>
            <div className="text-xs text-ink-muted">{s.duration_seconds}s · {s.camera}</div>
          </div>
          <div className="text-xs"><span className="text-ink-muted">VO:</span> {s.narration}</div>
          <div className="text-xs mt-1"><span className="text-ink-muted">VISUAL:</span> {s.visual_description}</div>
          {s.lower_third && <div className="text-xs mt-1"><span className="text-ink-muted">LOWER-THIRD:</span> {s.lower_third}</div>}
          <div className="text-[11px] text-ink-muted mt-1">music: {s.music_cue} · transition: {s.transition_to_next}</div>
        </div>
      ))}
    </div>
    {script.closing_cta && (
      <div className="rounded-md border border-brand-amber/30 bg-brand-amber/5 p-3">
        <div className="text-[11px] uppercase tracking-wider text-brand-amber mb-1">Closing CTA</div>
        <div className="font-display">{script.closing_cta}</div>
      </div>
    )}
  </div>
);

const StoryboardView = ({ storyboard }) => (
  <div className="grid md:grid-cols-2 gap-3">
    {(storyboard.boards || []).map((b, i) => (
      <div key={i} className="rounded-md border border-line bg-bg-elevated p-3" data-testid={`storyboard-card-${i}`}>
        <div className="flex items-center justify-between mb-1">
          <div className="font-display font-bold">{i + 1}. {b.title}</div>
          <div className="text-xs text-ink-muted">{b.duration_seconds}s</div>
        </div>
        <div className="text-xs text-ink-muted mb-2">{b.narration}</div>
        <div className="rounded bg-bg-elevated border border-line p-2 text-xs">
          <div className="text-[10px] uppercase tracking-wider text-brand-amber mb-1">Image prompt</div>
          <div className="break-words">{b.image_prompt}</div>
        </div>
        {b.motion_prompt && (
          <div className="rounded bg-bg-elevated border border-line p-2 text-xs mt-2">
            <div className="text-[10px] uppercase tracking-wider text-brand-blue mb-1">Motion prompt</div>
            <div className="break-words">{b.motion_prompt}</div>
          </div>
        )}
        <div className="text-[11px] text-ink-muted mt-2">camera: {b.camera} · music: {b.music_cue} · → {b.transition_to_next}</div>
      </div>
    ))}
  </div>
);
